// In-app Help & FAQ. Replaces the modal tutorial as the Help button's
// destination. Layout: left-side TOC, right-side scrollable content
// with collapsible FAQ blocks at the bottom. Mobile = single column.
import React from 'react';
import { Icon, Pill, Button, Card, Banner } from './ui.jsx';

const SECTIONS = [
  { id: 'quick-start',     title: 'Quick start' },
  { id: 'monthly-run',     title: 'Monthly commission run' },
  { id: 'approval-flow',   title: 'Approval workflow' },
  { id: 'plans',           title: 'Commission plans' },
  { id: 'customers-reps',  title: 'Customers & reps' },
  { id: 'reports',         title: 'Exporting reports' },
  { id: 'rep-view',        title: 'What reps see' },
  { id: 'troubleshooting', title: 'Troubleshooting' },
  { id: 'faq',             title: 'FAQ' },
];

export function HelpPage({ onNav, onStartTour }) {
  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[12px] text-neutral-500 uppercase tracking-[0.08em] font-semibold">Help</div>
          <h1 className="text-[28px] font-bold text-neutral-900 tracking-[-0.015em] mt-0.5">How to run commissions</h1>
          <div className="text-[14px] text-neutral-600 mt-1">Everything you need — from your first run to monthly close.</div>
        </div>
        {onStartTour && (
          <Button variant="secondary" icon="sparkle" onClick={onStartTour}>
            Take the 60-second tour
          </Button>
        )}
      </div>

      <div className="grid lg:grid-cols-[220px_1fr] gap-6">
        {/* Sticky table of contents */}
        <aside className="lg:sticky lg:top-6 lg:self-start">
          <div className="text-[10.5px] font-semibold uppercase tracking-[0.08em] text-neutral-500 mb-2">In this guide</div>
          <nav className="space-y-0.5">
            {SECTIONS.map(s => (
              <a key={s.id} href={`#${s.id}`}
                className="block text-[13px] text-neutral-700 hover:text-primary-700 hover:bg-primary-100/40 rounded-md px-2.5 py-1.5">
                {s.title}
              </a>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="space-y-10 min-w-0">
          <QuickStart onNav={onNav}/>
          <MonthlyRun onNav={onNav}/>
          <ApprovalFlow/>
          <PlansSection onNav={onNav}/>
          <CustomersReps onNav={onNav}/>
          <Reports/>
          <RepView/>
          <Troubleshooting/>
          <FAQ/>
        </div>
      </div>
    </div>
  );
}

// ---------- Sub-sections ----------

function H2({ id, children }) {
  return <h2 id={id} className="text-[20px] font-bold text-neutral-900 tracking-[-0.01em] scroll-mt-6">{children}</h2>;
}
function P({ children }) {
  return <p className="text-[14px] text-neutral-700 leading-relaxed">{children}</p>;
}
function Step({ n, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="flex-shrink-0 w-7 h-7 rounded-full bg-primary-700 text-white flex items-center justify-center text-[12px] font-bold">{n}</div>
      <div className="flex-1 min-w-0 pt-0.5">
        <div className="text-[14.5px] font-semibold text-neutral-900">{title}</div>
        <div className="text-[13.5px] text-neutral-600 mt-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}
function Tip({ children, tone = 'info' }) {
  const tones = {
    info:    'bg-blue-50 border-blue-200 text-blue-900',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    warn:    'bg-amber-50 border-amber-200 text-amber-900',
  };
  return <div className={`text-[13px] border rounded-md px-3 py-2 ${tones[tone]}`}>{children}</div>;
}

function QuickStart({ onNav }) {
  return (
    <section id="quick-start" className="space-y-4">
      <H2 id="quick-start">Quick start</H2>
      <P>If you're brand new, three things need to be in place before your first commission run:</P>
      <Card className="space-y-5">
        <Step n="1" title="Set up your commission plan">
          Open <button className="text-primary-700 font-semibold" onClick={() => onNav('s23')}>Commission plans</button>.
          A "Standard" plan is auto-created from your defaults — review the deductions
          (ACA, SICK) and tier rates. Click <b>Edit plan</b> to change anything. You can
          add multiple plans here if different rep groups have different rates.
        </Step>
        <Step n="2" title="Invite your team">
          <button className="text-primary-700 font-semibold" onClick={() => onNav('s22')}>Users → Add user</button>.
          Add admins (full access) and sales reps (see only their own commission). The system
          gives you a one-time temporary password to share with each person — they reset it
          on first sign-in.
        </Step>
        <Step n="3" title="Run your first commission run">
          Once plans + users are in place, click <button className="text-primary-700 font-semibold" onClick={() => onNav('s04')}>+ New run</button> and
          follow the four-step wizard. Full walkthrough below.
        </Step>
      </Card>
    </section>
  );
}

function MonthlyRun({ onNav }) {
  return (
    <section id="monthly-run" className="space-y-4">
      <H2 id="monthly-run">Monthly commission run — step by step</H2>
      <P>This is the workflow you'll repeat every month. Plan on 15 minutes start to finish.</P>

      <Card className="space-y-5">
        <Step n="1" title="Create the run">
          <span className="block">
            <button className="text-primary-700 font-semibold" onClick={() => onNav('s03')}>Commission Runs</button> → <b>+ New run</b> →
            give it a name (e.g. "March 2026"), pick the period, click <b>Next: Upload transaction file</b>.
            A run row is created in <i>Draft</i> status — you can come back to it any time.
          </span>
        </Step>
        <Step n="2" title="Upload the Transaction file">
          <span className="block">
            Drop your BoldStaff (or equivalent) Transaction export. The tool parses the file
            client-side, validates the columns against your tenant's column mapping, and
            aggregates time-entry rows into invoice-level rows.
          </span>
          <Tip tone="info" >
            <b>Heads-up.</b> The file's <code className="font-mono text-[12px] bg-blue-100 px-1 rounded">SSN</code> column
            (if present) is dropped at parse time and never stored or transmitted.
          </Tip>
        </Step>
        <Step n="3" title="Upload the AR aging file">
          <span className="block">
            Drop your Sage (or equivalent) aging export. The tool matches each AR row to
            transactions by <code className="font-mono text-[12px] bg-neutral-100 px-1 rounded">branch initials + invoice number</code>,
            then updates each invoice's <i>paid / outstanding / age</i> fields.
          </span>
          <Tip tone="warn">
            Invoices not in the AR file are marked <b>paid</b>. If your AR file is incomplete,
            re-upload it before approving.
          </Tip>
        </Step>
        <Step n="4" title="Assign customers to reps">
          <span className="block">
            Step 4 lists every customer in this run with their invoice count + GP. Pick a sales
            rep for each one. Assignments persist to the customer directory and apply to
            future runs automatically — you only need to do this once per customer.
          </span>
        </Step>
        <Step n="5" title="Click Calculate run">
          <span className="block">
            Walks every invoice through the rep's commission plan, computes commission, and
            navigates you to the run detail page. Numbers are live — change a rep assignment
            or rate and re-click Calculate to recompute.
          </span>
        </Step>
        <Step n="6" title="Finalize → Approve → Done">
          <span className="block">
            See the <a href="#approval-flow" className="text-primary-700 font-semibold">Approval workflow</a> section
            below for the three-state flow. The short version: <b>Finalize</b> locks the math,
            then a <b>different admin</b> approves it, then reps see their final commission.
          </span>
        </Step>
      </Card>
    </section>
  );
}

function ApprovalFlow() {
  return (
    <section id="approval-flow" className="space-y-4">
      <H2 id="approval-flow">Approval workflow</H2>
      <P>Every run flows through three states. Reps see different banners depending on which state the run is in.</P>

      <div className="grid sm:grid-cols-3 gap-3">
        <Card>
          <Pill tone="draft">Draft</Pill>
          <div className="text-[14px] font-semibold text-neutral-900 mt-2">Editable</div>
          <div className="text-[12.5px] text-neutral-600 mt-1.5">Admin uploads files, assigns reps, calculates. Numbers may change. Reps see <b>"still being calculated."</b></div>
        </Card>
        <Card>
          <Pill tone="paid">Finalized</Pill>
          <div className="text-[14px] font-semibold text-neutral-900 mt-2">Locked, awaiting approval</div>
          <div className="text-[12.5px] text-neutral-600 mt-1.5">Math is locked. A different admin must review and approve. Reps see <b>"pending approval."</b></div>
        </Card>
        <Card>
          <Pill tone="finalized">Approved</Pill>
          <div className="text-[14px] font-semibold text-neutral-900 mt-2">Final, payroll-ready</div>
          <div className="text-[12.5px] text-neutral-600 mt-1.5">Approver signed off. Reps see their <b>real commission.</b> Run is the source of truth for payroll.</div>
        </Card>
      </div>

      <Tip tone="info">
        <b>Segregation of duties:</b> the admin who created/uploaded the run cannot also approve it.
        A second admin must approve. This matches SOX expectations for any payroll-adjacent process.
      </Tip>

      <Card>
        <div className="text-[13px] font-semibold text-neutral-900 mb-2">Stepping back</div>
        <P>
          Any state can step back one level with a required reason — logged to the audit trail.
        </P>
        <ul className="text-[13.5px] text-neutral-700 mt-3 space-y-1.5 list-disc pl-5">
          <li><b>Approved</b> → unlock → returns to <i>Finalized</i>. Reason required.</li>
          <li><b>Finalized</b> → unlock → returns to <i>Draft</i>. Numbers become editable again.</li>
          <li><b>Draft</b> can be deleted entirely (admin only). Use <b>Delete run</b> on the run detail page — type DELETE to confirm.</li>
        </ul>
      </Card>
    </section>
  );
}

function PlansSection({ onNav }) {
  return (
    <section id="plans" className="space-y-4">
      <H2 id="plans">Commission plans</H2>
      <P>
        A plan defines the deductions, the tier ladder, and the basis on which tiers are decided.
        Every tenant starts with one <b>Standard</b> plan that mirrors your old Y1/Y2 rates.
      </P>

      <Card>
        <div className="text-[13px] font-semibold text-neutral-900 mb-2">Two tier modes</div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div className="border border-neutral-200 rounded-md p-3">
            <div className="text-[12.5px] font-bold text-primary-700">Per invoice (tenure)</div>
            <P>Each invoice picks one tier based on the customer's tenure. <b>Y1</b> = first 12 months of the customer's first invoice, <b>Y2+</b> = after.</P>
            <div className="text-[12px] text-neutral-500 mt-2">Best for: standard staffing comp plans.</div>
          </div>
          <div className="border border-neutral-200 rounded-md p-3">
            <div className="text-[12.5px] font-bold text-primary-700">Cumulative (volume)</div>
            <P>Rep's running base across the period walks through brackets. First $50K at 5%, next $50K at 7%, anything above at 9%.</P>
            <div className="text-[12px] text-neutral-500 mt-2">Best for: volume-incentive plans (senior reps, stretch goals).</div>
          </div>
        </div>
      </Card>

      <Card>
        <div className="text-[13px] font-semibold text-neutral-900 mb-2">Per-rep plan assignment</div>
        <P>
          Default is "every rep uses the tenant's default plan." To put one rep on a different plan
          (e.g. a senior rep on a cumulative-volume plan), open <button className="text-primary-700 font-semibold" onClick={() => onNav('s22')}>Users</button>,
          edit the rep, and pick a plan from the dropdown.
        </P>
      </Card>
    </section>
  );
}

function CustomersReps({ onNav }) {
  return (
    <section id="customers-reps" className="space-y-4">
      <H2 id="customers-reps">Customers & reps</H2>
      <P>
        Customers are auto-created on first Transaction upload. Each customer has one assigned
        sales rep — the assignment persists across runs.
      </P>
      <Card>
        <div className="text-[13px] font-semibold text-neutral-900 mb-2">Reassigning a rep</div>
        <P>
          Open <button className="text-primary-700 font-semibold" onClick={() => onNav('s11')}>Customers</button>,
          find the customer, and pick a different rep from the dropdown. Future runs will use
          the new rep automatically. To re-flow an in-flight run, click Calculate again.
        </P>
      </Card>
      <Tip tone="warn">
        <b>Unassigned customers</b> show as a warning badge in the sidebar and on the dashboard.
        Their invoices still appear in the run but don't count toward any rep's commission until
        you assign them.
      </Tip>
    </section>
  );
}

function Reports() {
  return (
    <section id="reports" className="space-y-4">
      <H2 id="reports">Exporting reports</H2>
      <P>
        Two formats are available — both generated client-side, no server roundtrip.
      </P>
      <div className="grid sm:grid-cols-2 gap-3">
        <Card>
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-md bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center font-bold text-[11px]">XLSX</div>
            <div className="text-[14px] font-semibold text-neutral-900">Excel</div>
          </div>
          <P>Two sheets: <b>Summary</b> (totals, KPIs, run period) and <b>Invoices</b> (every line with full detail). Use it for payroll import or downstream pivots.</P>
        </Card>
        <Card>
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-9 h-9 rounded-md bg-red-50 border border-red-200 text-red-700 flex items-center justify-center font-bold text-[11px]">PDF</div>
            <div className="text-[14px] font-semibold text-neutral-900">PDF</div>
          </div>
          <P>Print-ready. Page-numbered. Color-coded flags (red = overdue 90+, amber = carry-forward). Use it for share-out via email.</P>
        </Card>
      </div>
    </section>
  );
}

function RepView() {
  return (
    <section id="rep-view" className="space-y-4">
      <H2 id="rep-view">What sales reps see</H2>
      <P>
        Reps log in with their own email + password. They only ever see their own data — enforced
        in Postgres, not by the UI. Even if a rep opens DevTools and queries the API directly,
        they get nothing for other reps.
      </P>
      <Card>
        <div className="text-[13px] font-semibold text-neutral-900 mb-2">Rep tabs</div>
        <ul className="text-[13.5px] text-neutral-700 space-y-1.5 list-disc pl-5">
          <li><b>This Month</b> — hero commission number, breakdown showing GP → ACA → SICK → NET → rate → commission, list of their invoices.</li>
          <li><b>By Period</b> — same dashboard for any past month they participated in.</li>
          <li><b>History</b> — 12-month trend chart + per-period totals.</li>
          <li><b>Unpaid</b> — aging buckets (0-30 / 31-60 / 61-90 / 91-120 / 121+) with the actual outstanding invoices, oldest first.</li>
        </ul>
      </Card>
    </section>
  );
}

function Troubleshooting() {
  return (
    <section id="troubleshooting" className="space-y-4">
      <H2 id="troubleshooting">Troubleshooting</H2>

      <Card>
        <div className="text-[13.5px] font-semibold text-neutral-900">Calculate gives me 0 invoices saved</div>
        <P>Most likely cause: column mapping doesn't match your file. Check the validation cards on the upload step — every required column must show ✓.</P>
      </Card>

      <Card>
        <div className="text-[13.5px] font-semibold text-neutral-900">"Could not find the X column" error</div>
        <P>Schema cache is stale. Run <code className="font-mono text-[12px] bg-neutral-100 px-1 rounded">notify pgrst, 'reload schema';</code> in the Supabase SQL editor and retry.</P>
      </Card>

      <Card>
        <div className="text-[13.5px] font-semibold text-neutral-900">Approve button is disabled</div>
        <P>You created this run, so you can't also approve it. Have a different admin sign in and approve. (Segregation of duties — see Approval workflow.)</P>
      </Card>

      <Card>
        <div className="text-[13.5px] font-semibold text-neutral-900">Rep can't see their commission</div>
        <P>Run is probably still in <i>Draft</i>. Reps only see "real" numbers once a run is <b>Approved</b>. Earlier states show "pending" or "still being calculated."</P>
      </Card>

      <Card>
        <div className="text-[13.5px] font-semibold text-neutral-900">Email rate limit on password reset</div>
        <P>Supabase free tier limits auth emails to ~4/hour. For internal use, set the password directly via Supabase dashboard → Authentication → Users → Edit user.</P>
      </Card>
    </section>
  );
}

function FAQ() {
  const items = [
    {
      q: 'Who can sign in?',
      a: 'Only emails explicitly added to your team\'s user list by an admin. There\'s no public signup — the login screen rejects any email not on the allowlist.',
    },
    {
      q: 'What file formats does the tool accept?',
      a: 'Transaction files: .xlsx and .csv. AR aging reports: .xlsx, .csv, and HTML-disguised-as-.xls (common with Sage exports). The column mapping is configured per tenant.',
    },
    {
      q: 'Can different reps have different commission rates?',
      a: 'Yes. You can either give them per-Y1/Y2 overrides on their user record OR assign them to a different commission plan entirely. Plans support per-invoice tenure tiers and cumulative-volume tiers.',
    },
    {
      q: 'What happens to invoices that are still unpaid at month-end?',
      a: 'They\'re flagged with their outstanding balance and age. Anything over 90 days old triggers an "overdue" banner on the rep\'s dashboard for escalation.',
    },
    {
      q: 'Can I re-run a finalized month?',
      a: 'Yes — click Unlock on the run detail page. The unlock is logged in the audit trail with timestamp and reason. The run returns to draft, and you can recalculate.',
    },
    {
      q: 'Where does the data live?',
      a: 'On Supabase Postgres (running on AWS underneath). All schema changes are checked into Git as numbered SQL migrations. The app is portable to self-managed AWS RDS when ready.',
    },
    {
      q: 'How is sensitive data protected?',
      a: 'Every table has Row-Level Security on. Reps can only query their own invoices — enforced in the database, not just the UI. SSN columns are stripped at parse time and never stored. Tenant logo + data are isolated per tenant.',
    },
    {
      q: 'Can I add multiple LLCs / entities?',
      a: 'Yes — each is a "tenant" with its own column mapping, rate structure, plans, users, and logo. Switch between them via the tenant picker in the top-right.',
    },
  ];
  const [open, setOpen] = React.useState(0);
  return (
    <section id="faq" className="space-y-4">
      <H2 id="faq">FAQ</H2>
      <div className="space-y-2.5">
        {items.map((it, i) => {
          const active = open === i;
          return (
            <div key={i} className={`bg-white rounded-xl border transition-all overflow-hidden ${active ? 'border-primary-300 shadow-sm' : 'border-neutral-200'}`}>
              <button onClick={() => setOpen(active ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
                <span className="text-[14.5px] font-semibold text-neutral-900">{it.q}</span>
                <span className={`w-7 h-7 rounded-full border flex items-center justify-center transition-transform ${active ? 'rotate-45 bg-primary-700 text-white border-primary-700' : 'bg-canvas border-neutral-200 text-neutral-500'}`}>
                  <Icon name="plus" size={13}/>
                </span>
              </button>
              {active && (
                <div className="px-5 pb-4 text-[13.5px] text-neutral-600 leading-relaxed border-t border-neutral-100 pt-3">
                  {it.a}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <Banner tone="info" icon="info" title="Still stuck?">
        Reach out to your admin or email <a href="mailto:hello@staffology.app" className="font-semibold underline">hello@staffology.app</a> — we reply within a business day.
      </Banner>
    </section>
  );
}
